import * as React from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  AccessReportsAdminDashboardTabEnum,
  AccessReportsAdminDashboardModalTypeEnum,
  AccessReportsAdminVersionTypeEnum
} from "@app/access-reports/admin";
import { isEqual } from "lodash";
import { Modal } from "@app/shared/Modals";
import { JSX } from "react";
import { formatDateCustom } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButtonTypes, QESButton } from "@app/shared/qes-standards/Button";
import { QESRadioButton } from "@app/shared/qes-standards/forms/RadioButton";
import { QESRadioGroup } from "@app/shared/qes-standards/forms/RadioGroup";
import { AccessReportsAdminCoverPageTab } from "@app/access-reports/admin/AccessReportsAdminCoverPageTab";
import { AccessReportsAdminPrintSettingsTab } from "@app/access-reports/admin/AccessReportsAdminPrintSettingsTab";
import { AccessReportsAdminProductsTab } from "@app/access-reports/admin/AccessReportsAdminProductsTab";
import { AccessReportsPrintOptions } from "@app/access-reports/shared/accessReportsPrintOptions";
import { AccessReportsWizardElement } from "@app/access-reports/shared/accessReportsWizardElement";
import { AccessReportsWizardModel } from "@app/access-reports/shared/accessReportsWizardModel";
import { AccessReportsWizardOption } from "@app/access-reports/shared/accessReportsWizardOption";
import { AccessReportsWizardProduct } from "@app/access-reports/shared/accessReportsWizardProduct";
import { AccessReportsWizardTemplate } from "@app/access-reports/shared/accessReportsWizardTemplate";
import { AccessReportsWizardVersionModel } from "@app/access-reports/shared/accessReportsWizardVersionModel";

type AccessReportsAdminDashboardProps = RouteComponentProps<unknown> & {
  accessReportsWizard: AccessReportsWizardModel;
  checkedOutUsername: string;
  disableUserActions: boolean;
  isCheckedOutByCurrentUser: boolean;
  loading: boolean
  onActivateClick: () => void;
  onCheckInClick: () => void;
  onCheckOutClick: () => void;
  onCoverPageDefaultValueChange: (elementId: string, event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => void;
  onCoverPageToggle: (elementId: string, key: keyof AccessReportsWizardElement) => void;
  onCreateNewVersionClick: () => void;
  onDeleteVersionClick: () => void;
  onPrintOptionsChange: (formGroupId: number, key: keyof AccessReportsPrintOptions, defaultValue: string | boolean) => void;
  onRemoveProductsClick: (products: AccessReportsWizardProduct[]) => void;
  onResetPrintSettingsClick: () => void;
  onSelectVersionTypeClick: (selectedVersion: AccessReportsAdminVersionTypeEnum) => void;
  onSaveClick: () => void;
  onSaveLogosClick: (logos: AccessReportsWizardOption[], reloadPage: boolean) => void;
  onSaveProductsClick: (productTypes: AccessReportsWizardOption[]) => void;
  onSaveProductTypesClick: (productTypes: AccessReportsWizardOption[]) => void;
  selectedVersion: AccessReportsWizardVersionModel;
  selectedVersionType: AccessReportsAdminVersionTypeEnum;
}

const AccessReportsAdminDashboard: React.FC<AccessReportsAdminDashboardProps> = (props: AccessReportsAdminDashboardProps) => {
  const deleteButtonRef = React.useRef<HTMLButtonElement>(null);
  const activateButtonRef = React.useRef<HTMLButtonElement>(null);
  const [modalType, setModalType] = React.useState<AccessReportsAdminDashboardModalTypeEnum>(null);

  const disableActivation = (): boolean => {
    // compare the currently-activated version with the currently-SAVED in-progress version
    const currentlyActivatedTemplate: AccessReportsWizardTemplate = props.accessReportsWizard.accessReportWizardVersions.find(x => !!x.dateActivated)?.template;
    const inProgressTemplate: AccessReportsWizardTemplate = props.accessReportsWizard.accessReportWizardVersions.find(x => !x.dateActivated)?.template;
    const equalTemplates: boolean = isEqual(currentlyActivatedTemplate, inProgressTemplate);

    return equalTemplates;
  }

  const getModal = (): JSX.Element => {
    let modalContent: JSX.Element;
    let confirmButtonType: QESButtonTypes;
    let triggerElement: React.RefObject<HTMLButtonElement>;

    switch (modalType) {
      case AccessReportsAdminDashboardModalTypeEnum.ActivateVersion:
        modalContent = (
          <>
            <h4 className="header-s">Are you sure you want to activate the current version?</h4>
            <p className="copy-text-default">
              This version will be utilized in the access reports wizard.
              <br />
              The previously-activated version will be archived.
            </p>
          </>
        );
        confirmButtonType = "primary";
        triggerElement = activateButtonRef;

        break;
      case AccessReportsAdminDashboardModalTypeEnum.DeleteVersion:
        modalContent = (
          <>
            <h4 className="header-s">Are you sure you want to delete the in-progress version?</h4>
            <p className="copy-text-default">
              You will <strong>never</strong> be able to retrieve this version&apos;s template again and all changes will be lost.
            </p>
          </>
        );
        confirmButtonType = "primary-danger";
        triggerElement = deleteButtonRef;

        break;
      default:
        break;
    }

    return (
      <Modal
        isVisible={!!modalType}
        closeModal={() => onHideModalClick(false)}
        content={{
          body: modalContent,
          cancelButtonText: "Cancel",
          confirmButtonAction: () => onHideModalClick(true),
          confirmButtonText: "Confirm",
          confirmButtonType: confirmButtonType,
          title: "Warning",
        }}
        hideBodyScroll={true}
        triggerElement={triggerElement}
      />
    );
  }

  const getDashboardBody = (): JSX.Element => {
    return (
      <React.Fragment>
        {getVersionSelectionSection()}

        {!!props.selectedVersionType &&
          getSelectedVersionTypeSection()
        }
      </React.Fragment>
    );
  }

  const getQESAlertMessage = (): JSX.Element => {
    if (!!props.selectedVersion) {
      switch (props.selectedVersionType) {
        case AccessReportsAdminVersionTypeEnum.CurrentVersion:
          return (
            <>
              You are viewing the currently-activated access reports wizard version in <strong>readonly mode</strong>.
              <br />
              In order to make modifications, select the in-progress version and see if it is available for check-out.
            </>
          );
        case AccessReportsAdminVersionTypeEnum.InProgressVersion:
          if (!!props.selectedVersion.checkedOutDate) {
            if (props.isCheckedOutByCurrentUser) {
              // checked out by current user
              return (
                <>
                  You have <strong>checked-out</strong> the currently in-progress version of the access reports wizard.
                  <br />
                  Other users may not edit this version until you have checked it in.
                </>
              );
            } else {
              // not checked out by current user
              return (
                <>
                  The in-progress version of the access reports wizard is currently checked-out by {props.checkedOutUsername}.
                  <br />
                  At the moment, you may only view this version in <strong>readonly mode</strong>.
                </>
              );
            }
          } else {
            // not checked out
            return (
              <>
                You are viewing the in-progress version of the access reports wizard in <strong>readonly mode</strong>.
                <br />
                In order to make modifications, please check-out the template.
              </>
            );
          }
        default:
          break;
      }
    } else {
      return (
        <>
          An in-progress version of the access reports does not currently exist.
          <br />
          If you would like to create one, click on the &quot;create new version&quot; button.
        </>
      );
    }
  }

  const getSelectedVersionTypeSection = (): JSX.Element => {
    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12">
            <QESAlert classNames="mb-3" type="warning" persist={true}>
              <p className="mb-0">
                {getQESAlertMessage()}
              </p>
            </QESAlert>
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between mb-3">
              <label className="align-self-center mb-0 copy-text-default">
                {props.selectedVersion && (
                  <>
                    Date last updated:
                    &nbsp;
                    {props.selectedVersion.dateLastUpdated ?
                      formatDateCustom(props.selectedVersion.dateLastUpdated, "MMM DD YYYY h:mm:ss A") :
                      "--"
                    }
                  </>
                )}
              </label>

              {(props.selectedVersionType !== AccessReportsAdminVersionTypeEnum.CurrentVersion) &&
                getVersionActionButtons()
              }
            </div>
          </div>
        </div>

        {!!props.selectedVersion && (
          <div className="row">
            <div className="col-12">
              <ul
                className="nav nav-tabs"
                id="access-reports-admin__dashboard--tabs"
              >
                {Object.values(AccessReportsAdminDashboardTabEnum).map((tab, index) => (
                  <li className="nav-item" key={index}>
                    <a
                      className={tab === AccessReportsAdminDashboardTabEnum.Products ? "nav-link active" : "nav-link"}
                      id={`access-reports-admin__dashboard--tab-${index}`}
                      data-bs-toggle="tab"
                      href={`#${getTabId(tab)}`}
                    >
                      {tab}
                    </a>
                  </li>
                ))}
              </ul>

              <div className="tab-content">
                <AccessReportsAdminProductsTab
                  accessReportsWizardTemplate={props.selectedVersion?.template}
                  disableUserActions={props.disableUserActions}
                  onRemoveProductsClick={props.onRemoveProductsClick}
                  onSaveProductsClick={props.onSaveProductsClick}
                  onSaveProductTypesClick={props.onSaveProductTypesClick}
                  selectedVersionType={props.selectedVersionType}
                />

                <AccessReportsAdminCoverPageTab
                  accessReportsWizard={props.accessReportsWizard}
                  disableUserActions={props.disableUserActions}
                  onCoverPageDefaultValueChange={props.onCoverPageDefaultValueChange}
                  onCoverPageToggle={props.onCoverPageToggle}
                  onSaveClick={props.onSaveClick}
                  onSaveLogosClick={props.onSaveLogosClick}
                  selectedVersion={props.selectedVersion}
                />

                <AccessReportsAdminPrintSettingsTab
                  accessReportsWizard={props.accessReportsWizard}
                  disableUserActions={props.disableUserActions}
                  onPrintOptionsChange={props.onPrintOptionsChange}
                  onResetPrintSettingsClick={props.onResetPrintSettingsClick}
                  onSaveClick={props.onSaveClick}
                  selectedVersion={props.selectedVersion}
                />
              </div>
            </div>
          </div>
        )}
      </React.Fragment>
    );
  }

  const getTabId = (tab: AccessReportsAdminDashboardTabEnum): string => {
    const formattedTab: string[] = tab.split(/(\s+)/).filter((x) => {
      return x.trim().length > 0;
    });

    return formattedTab.join("-").toLowerCase();
  }

  const getVersionSelectionSection = (): JSX.Element => {
    const getVersionTypeDescription = (versionType: AccessReportsAdminVersionTypeEnum): string => {
      switch (versionType) {
        case AccessReportsAdminVersionTypeEnum.CurrentVersion:
          return "The activated version of the template currently utilized by the access reports wizard";
        case AccessReportsAdminVersionTypeEnum.InProgressVersion:
          return "The not-yet activated version of the template that is currently being worked on";
        default:
          return "";
      }
    }

    return (
      <>
        <div className="row">
          <div className="col-md-2">
            <label
              className="label-s"
            >
              Select a version
            </label>
          </div>
          {Object.values(AccessReportsAdminVersionTypeEnum).map((versionType, index) => (
            <div className="col-md-3" key={index}>
              <QESRadioGroup renderInline={true} groupContainerClass="mb-2">
                <QESRadioButton
                  label={versionType}
                  labelClass="label-s"
                  descriptiveText={getVersionTypeDescription(versionType)}
                  checked={props.selectedVersionType === versionType}
                  onChange={() => props.onSelectVersionTypeClick(versionType)}
                  testId={`access-reports-admin__dashboard--version-type-${index}`}
                  name="access-reports-version-type"
                  value={versionType}
                />
              </QESRadioGroup>
            </div>
          ))}
        </div>
      </>
    );
  }

  const getVersionActionButtons = (): JSX.Element => {
    if (!!props.selectedVersion) {
      if (props.isCheckedOutByCurrentUser) {
        return (
          <div className="align-self-center">
            <QESButton
              qesType="primary-danger"
              qesTestId={"access-reports-admin__dashboard--delete"}
              onClick={() => showModal(AccessReportsAdminDashboardModalTypeEnum.DeleteVersion)}
              qesRef={deleteButtonRef}
            >
              Delete
            </QESButton>

            <QESButton
              className="ms-3"
              disabled={disableActivation()}
              qesType="primary"
              qesTestId={"access-reports-admin__dashboard--activate"}
              onClick={() => showModal(AccessReportsAdminDashboardModalTypeEnum.ActivateVersion)}
              qesRef={activateButtonRef}
            >
              Activate
            </QESButton>

            <QESButton
              className="ms-3"
              qesType="primary"
              qesTestId={"access-reports-admin__dashboard--check-in"}
              onClick={() => props.onCheckInClick()}
            >
              Check In
            </QESButton>
          </div>
        );
      } else {
        return (
          <QESButton
            className="align-self-center"
            disabled={!!props.selectedVersion.checkedOutDate}
            qesType="primary"
            qesTestId={"access-reports-admin__dashboard--check-out"}
            onClick={() => props.onCheckOutClick()}
          >
            Check Out
          </QESButton>
        );
      }
    } else {
      return (
        <QESButton
          className="align-self-center"
          qesType="primary"
          qesTestId={"access-reports-admin__dashboard--create-new-version"}
          onClick={() => props.onCreateNewVersionClick()}
        >
          Create New Version
        </QESButton>
      );
    }
  }

  const onHideModalClick = (confirmed: boolean): void => {
    if (confirmed) {
      switch (modalType) {
        case AccessReportsAdminDashboardModalTypeEnum.ActivateVersion:
          props.onActivateClick();
          break;
        case AccessReportsAdminDashboardModalTypeEnum.DeleteVersion:
          props.onDeleteVersionClick();
          break;
        default:
          break;
      }
    }

    setModalType(null);
  }

  const showModal = (newModalType: AccessReportsAdminDashboardModalTypeEnum): void => {
    setModalType(newModalType);
  }

  return (
    <Page className="bg-white" documentTitle="Access Reports Admin">
      <PageInner>
        <PageSection>
          {!!modalType &&
            getModal()
          }

          <div className="row">
            <div className="col-12">
              <h2 className="mt-3 mb-3 header-l demibold">
                Access Report Admin
              </h2>
            </div>
          </div>

          {!props.loading ? (
            getDashboardBody()
          ) : (
            <div className="row">
              <div className="col-12">
                <div className="text-center">
                  <LoadingSpinner />
                </div>
              </div>
            </div>
          )}
        </PageSection>
      </PageInner>
    </Page>
  );
}

export { AccessReportsAdminDashboard };
